import { useState, ReactNode, useMemo, useCallback } from "react";
import { PreorderContext } from "context/preorder-context";

export const PreorderProvider = ({ children }: { children: ReactNode }) => {
    const [currentQuantities, setCurrentQuantities] = useState<number[][]>([]);
    const [selectedArticleHasMandatoryMenu, setSelectedArticleHasMandatoryMenu] = useState(false);
    const [groupNames, setGroupNames] = useState<string[]>([]);
    const [groupValidityMap, setGroupValidityMap] = useState<Record<string | number, boolean>>({});
    const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

    const currentTotalQuantity = useMemo(
        () =>
            currentQuantities.reduce(
                (sum, term) => sum + term.reduce((sum, term) => sum + term, 0),
                0
            ),
        [currentQuantities]
    );

    const isPreorderValid = useMemo(() => {
        if (selectedArticleHasMandatoryMenu && currentTotalQuantity === 0) return false;
        if (Object.keys(groupValidityMap).length === 0) return true;
        return Object.values(groupValidityMap).every((isValid) => isValid);
    }, [currentTotalQuantity, groupValidityMap, selectedArticleHasMandatoryMenu]);

    const updateGroupValidity = useCallback((groupId: string | number, isValid: boolean) => {
        setGroupValidityMap((prev) => {
            if (prev[groupId] === isValid) return prev;
            return { ...prev, [groupId]: isValid };
        });
    }, []);

    const nextInvalidGroup = useMemo(() => {
        if (isPreorderValid) return null;

        const invalidGroupId = Object.entries(groupValidityMap).find(
            ([, isValid]) => !isValid
        )?.[0];

        // If no specific invalid group found but we know preorder is invalid,
        // it means we need to select any item (mandatory menu case)
        if (!invalidGroupId && selectedArticleHasMandatoryMenu && currentTotalQuantity === 0) {
            return { name: groupNames[0], index: 0 }; // Return the first group name or null
        }

        // If we found an invalid group ID, find its index and then get the corresponding name
        if (invalidGroupId) {
            const groupIndex = parseInt(invalidGroupId, 10);
            return { name: groupNames[groupIndex], index: groupIndex };
        }

        return null;
    }, [
        isPreorderValid,
        groupValidityMap,
        groupNames,
        selectedArticleHasMandatoryMenu,
        currentTotalQuantity,
    ]);

    return (
        <PreorderContext.Provider
            value={{
                currentQuantities,
                setCurrentQuantities,
                currentTotalQuantity,
                groupValidityMap,
                isPreorderValid,
                updateGroupValidity,
                selectedArticleHasMandatoryMenu,
                setSelectedArticleHasMandatoryMenu,
                setGroupNames,
                nextInvalidGroup: nextInvalidGroup,
                activeTabIndex,
                setActiveTabIndex,
            }}
        >
            {children}
        </PreorderContext.Provider>
    );
};
